import { useState, useRef, useEffect, useCallback, useMemo } from "react";

// ─── Données techniques ──────────────────────────────────────────────────────
const TECHNIQUES = [
  {
    id: 1,
    name: "Cohérence Cardiaque (6-6)",
    shape: "circle",
    cycleDuration: 12,
    phases: [
      { name: "inhale", duration: 6, scale: 1.4, label: "Inspire" },
      { name: "exhale", duration: 6, scale: 1.0, label: "Expire" },
    ],
  },
  {
    id: 2,
    name: "Carré (4-4-4-4)",
    shape: "square",
    cycleDuration: 16,
    phases: [
      { name: "inhale", duration: 4, scale: 1.25, label: "Inspire" },
      { name: "hold1", duration: 4, scale: 1.25, label: "Retiens" },
      { name: "exhale", duration: 4, scale: 0.75, label: "Expire" },
      { name: "hold2", duration: 4, scale: 0.75, label: "Retiens" },
    ],
  },
  {
    id: 3,
    name: "Soupir Physiologique",
    shape: "circle",
    cycleDuration: 15,
    phases: [
      {
        name: "inhale1",
        duration: 3.5,
        scale: 1.3,
        label: "Grande inspiration",
      },
      { name: "pause", duration: 1, scale: 1.3, label: "Pause" },
      {
        name: "inhale2",
        duration: 2,
        scale: 1.4,
        label: "Petite inspiration",
      },
      {
        name: "exhale",
        duration: 8.5,
        scale: 0.7,
        label: "Longue expiration",
      },
    ],
  },
  {
    id: 4,
    name: "Longue Expiration (3-7)",
    shape: "circle",
    cycleDuration: 10,
    phases: [
      { name: "inhale", duration: 3, scale: 1.4, label: "Inspire" },
      { name: "exhale", duration: 7, scale: 1.0, label: "Expire" },
    ],
  },
  {
    id: 5,
    name: "4-7-8",
    shape: "circle",
    cycleDuration: 19,
    phases: [
      { name: "inhale", duration: 4, scale: 1.4, label: "Inspire" },
      { name: "hold", duration: 7, scale: 1.4, label: "Retiens" },
      { name: "exhale", duration: 8, scale: 1.0, label: "Expire" },
    ],
  },
  {
    id: 7,
    name: "Triangle (4-4-8)",
    shape: "triangle",
    cycleDuration: 16,
    phases: [
      { name: "inhale", duration: 4, scale: 1.25, label: "Inspire" },
      { name: "hold", duration: 4, scale: 1.25, label: "Retiens" },
      { name: "exhale", duration: 8, scale: 0.7, label: "Longue expiration" },
    ],
  },
  {
    id: 9,
    name: "Grande Ventilation (8-8)",
    shape: "circle",
    cycleDuration: 16,
    phases: [
      { name: "inhale", duration: 8, scale: 1.4, label: "Inspire" },
      { name: "exhale", duration: 8, scale: 1.0, label: "Expire" },
    ],
  },
];

const PHASE_COLORS = {
  inhale: { main: "#76A5A7", secondary: "#5D8A87", track: "#D7E7E5" },
  inhale1: { main: "#76A5A7", secondary: "#5D8A87", track: "#D7E7E5" },
  inhale2: { main: "#76A5A7", secondary: "#5D8A87", track: "#D7E7E5" },
  exhale: { main: "#61837A", secondary: "#55736B", track: "#DDE7E2" },
  hold: { main: "#76A5A7", secondary: "#5D8A87", track: "#D7E7E5" },
  hold1: { main: "#76A5A7", secondary: "#5D8A87", track: "#D7E7E5" },
  hold2: { main: "#76A5A7", secondary: "#5D8A87", track: "#D7E7E5" },
  pause: { main: "#76A5A7", secondary: "#5D8A87", track: "#D7E7E5" },
};

const MODE_INFO = {
  "gif-transparent":  { label: "GIF transparent contour", previewBg: "transparent" },
  "webm-transparent": { label: "WebM transparent (timeline Canva)", previewBg: "transparent" },
  "webm-green":       { label: "WebM fond vert Canva", previewBg: "green" },
};

// ─── Réglages export ─────────────────────────────────────────────────────────
const PREVIEW_SIZE = 800;
const WEBM_SIZE = 800;
const GIF_SIZE = 640;
const WEBM_FPS = 30;
const GIF_FPS = 18;

// ─── Helpers ─────────────────────────────────────────────────────────────────
const smoothstep = (x) => x * x * (3 - 2 * x);

function getPhaseState(t, phases) {
  const cycleDur = phases.reduce((sum, p) => sum + p.duration, 0);
  const ct = t % cycleDur;

  let acc = 0;
  for (let i = 0; i < phases.length; i++) {
    if (ct < acc + phases[i].duration) {
      return {
        phase: phases[i],
        index: i,
        elapsed: ct - acc,
        duration: phases[i].duration,
      };
    }
    acc += phases[i].duration;
  }

  return { phase: phases[0], index: 0, elapsed: 0, duration: phases[0].duration };
}

function getScale(t, phases) {
  const { phase, index, elapsed, duration } = getPhaseState(t, phases);
  const prev = phases[index === 0 ? phases.length - 1 : index - 1];
  const p = smoothstep(Math.min(elapsed / duration, 1));
  return (prev.scale ?? 1) + ((phase.scale ?? 1) - (prev.scale ?? 1)) * p;
}

function splitShapeLabel(label) {
  const words = String(label || "")
    .trim()
    .toUpperCase()
    .split(/\s+/)
    .filter(Boolean);

  if (words.length <= 1) return [words];

  if (words.length === 2) {
    return [[words[0], words[1]], [words.join(" ")]];
  }

  if (words.length === 3) {
    return [
      [`${words[0]} ${words[1]}`, words[2]],
      [words[0], `${words[1]} ${words[2]}`],
      [words.join(" ")],
    ];
  }

  const mid = Math.ceil(words.length / 2);
  return [
    [words.slice(0, mid).join(" "), words.slice(mid).join(" ")],
    [words.join(" ")],
  ];
}

function getShapeTextBox(shape, radius, cx, cy) {
  const r = Math.max(1, radius);

  if (shape === "circle") {
    return { width: r * 1.34, height: r * 0.92, centerY: cy };
  }

  if (shape === "square") {
    return { width: r * 1.46, height: r * 1.02, centerY: cy };
  }

  return { width: r * 1.3, height: r * 0.72, centerY: cy + r * 0.21 };
}

function clipShapePath(ctx, shape, radius, cx, cy) {
  ctx.beginPath();

  if (shape === "circle") {
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    return;
  }

  if (shape === "square") {
    const h = radius * 0.95;
    const cr = h * 0.22;
    ctx.roundRect(cx - h, cy - h, h * 2, h * 2, cr);
    return;
  }

  const hw = radius * 1.05;
  const hh = radius * 0.88;
  ctx.moveTo(cx, cy - hh);
  ctx.lineTo(cx + hw, cy + hh);
  ctx.lineTo(cx - hw, cy + hh);
  ctx.closePath();
}

function drawShapeLabel(
  ctx,
  {
    shape,
    cx,
    cy,
    radius,
    label,
    fillStyle,
    shadowColor,
    shadowBlur,
    strokeStyle = null,
    strokeWidth = 0,
  }
) {
  const variants = splitShapeLabel(label);
  if (!variants.length) return;

  const box = getShapeTextBox(shape, radius, cx, cy);
  const maxWidth = box.width;
  const maxHeight = box.height;

  let bestLayout = null;

  for (const lines of variants) {
    const lineCount = lines.length;

    const fontRatio =
      shape === "triangle"
        ? lineCount === 1
          ? 0.24
          : lineCount === 2
            ? 0.165
            : 0.14
        : shape === "square"
          ? lineCount === 1
            ? 0.23
            : lineCount === 2
              ? 0.16
              : 0.135
          : lineCount === 1
            ? 0.25
            : lineCount === 2
              ? 0.17
              : 0.14;

    const fontSize = Math.max(12, radius * fontRatio);
    const lineHeight = fontSize * 1.04;
    const blockHeight = lineHeight * lineCount;

    if (blockHeight > maxHeight) continue;

    ctx.save();
    ctx.font = `700 ${fontSize}px "DM Sans", "Inter", system-ui, sans-serif`;
    const widestLine = Math.max(...lines.map((line) => ctx.measureText(line).width));
    ctx.restore();

    if (widestLine > maxWidth) continue;

    bestLayout = { lines, fontSize, lineHeight, blockHeight };
    break;
  }

  if (!bestLayout) {
    const fallbackLines = variants[0];
    const fallbackSize = Math.max(11, radius * 0.125);
    bestLayout = {
      lines: fallbackLines,
      fontSize: fallbackSize,
      lineHeight: fallbackSize * 1.02,
      blockHeight: fallbackSize * 1.02 * fallbackLines.length,
    };
  }

  const startY =
    box.centerY - bestLayout.blockHeight / 2 + bestLayout.lineHeight / 2;

  ctx.save();
  clipShapePath(ctx, shape, radius, cx, cy);
  ctx.clip();

  ctx.font = `700 ${bestLayout.fontSize}px "DM Sans", "Inter", system-ui, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = fillStyle;
  ctx.shadowColor = shadowColor;
  ctx.shadowBlur = shadowBlur;

  bestLayout.lines.forEach((line, index) => {
    const y = startY + index * bestLayout.lineHeight;

    if (strokeStyle && strokeWidth > 0) {
      ctx.lineJoin = "round";
      ctx.miterLimit = 2;
      ctx.strokeStyle = strokeStyle;
      ctx.lineWidth = strokeWidth;
      ctx.strokeText(line, cx, y);
    }

    ctx.fillText(line, cx, y);
  });

  ctx.restore();
}

// ─── Dessin d'un frame ───────────────────────────────────────────────────────
function drawFrame(ctx, size, t, technique, mode) {
  const cx = size / 2;
  const cy = size / 2;
  const baseR = size * 0.21;

const isTransparentGif = mode === "gif-transparent";
const isWebmMode =
  mode === "webm-green" ||
  mode === "webm-transparent" ||
  mode === "compose";

ctx.clearRect(0, 0, size, size);

// On ne remplit le fond vert QUE pour le mode direct (pas pour compose)
if (mode === "webm-green") {
  ctx.fillStyle = "#00ff66";
  ctx.fillRect(0, 0, size, size);
}

  const { phase, elapsed, duration } = getPhaseState(t, technique.phases);
  const phaseProgress = duration > 0 ? Math.min(elapsed / duration, 1) : 0;
  const scale = getScale(t, technique.phases);
  const r = baseR * scale;
  const colors = PHASE_COLORS[phase.name] || PHASE_COLORS.inhale;
  const { shape } = technique;

  const makePath = (radius) => {
    ctx.beginPath();

    if (shape === "circle") {
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
      return;
    }

    if (shape === "square") {
      const h = radius * 0.95;
      const cr = h * 0.22;
      ctx.roundRect(cx - h, cy - h, h * 2, h * 2, cr);
      return;
    }

    const hw = radius * 1.05;
    const hh = radius * 0.88;
    ctx.moveTo(cx, cy - hh);
    ctx.lineTo(cx + hw, cy + hh);
    ctx.lineTo(cx - hw, cy + hh);
    ctx.closePath();
  };

  // Fill interne
  if (isWebmMode) {
    ctx.save();
    makePath(r);
    ctx.clip();

    const fillGrad = ctx.createRadialGradient(
      cx,
      cy - r * 0.14,
      r * 0.06,
      cx,
      cy,
      r * 1.02
    );
fillGrad.addColorStop(0, "rgba(86, 108, 142, 0.045)");
fillGrad.addColorStop(0.42, "rgba(44, 60, 92, 0.025)");
fillGrad.addColorStop(1, "rgba(18, 28, 48, 0.010)");
    ctx.fillStyle = fillGrad;
    ctx.fill();

    // Presque aucun voile
    const innerMist = ctx.createLinearGradient(cx, cy - r, cx, cy + r);
    innerMist.addColorStop(0, "rgba(255,255,255,0.006)");
    innerMist.addColorStop(0.5, "rgba(255,255,255,0.003)");
    innerMist.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = innerMist;
    ctx.fill();

    // Reflet haut très discret
    const hlGrad = ctx.createLinearGradient(cx, cy - r, cx, cy + r * 0.14);
    hlGrad.addColorStop(0, "rgba(255,255,255,0.08)");
    hlGrad.addColorStop(0.22, "rgba(255,255,255,0.03)");
    hlGrad.addColorStop(0.48, "rgba(255,255,255,0.01)");
    hlGrad.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = hlGrad;
    ctx.fill();

    ctx.restore();
  }

  // Bordure principale
  ctx.save();
  ctx.shadowColor = "rgba(126, 198, 205, 0.12)";
  ctx.shadowBlur = size * 0.006;
  makePath(r);
  ctx.strokeStyle = "rgba(168, 224, 229, 0.72)";
  ctx.lineWidth = Math.max(1.25, size * 0.0038);
  ctx.stroke();
  ctx.restore();

  // Ring pour les formes cercle
  if (shape === "circle") {
    const ringR = r * 1.19;
    const trackW = size * 0.0088;

    // Base du ring plus discrète mais toujours visible
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, ringR, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(82, 108, 132, 0.18)";
    ctx.lineWidth = trackW;
    ctx.stroke();
    ctx.restore();

    // Léger voile de base pour donner de la matière
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, ringR, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(170, 220, 224, 0.05)";
    ctx.lineWidth = trackW * 0.55;
    ctx.stroke();
    ctx.restore();

    // Progression plus nette mais moins épaisse
    if (phaseProgress > 0.005) {
      ctx.save();
      ctx.beginPath();
      ctx.arc(
        cx,
        cy,
        ringR,
        -Math.PI / 2,
        -Math.PI / 2 + phaseProgress * Math.PI * 2
      );
      ctx.strokeStyle = "rgba(132, 206, 212, 0.95)";
      ctx.lineWidth = trackW;
      ctx.lineCap = "round";
      ctx.shadowColor = "rgba(132, 206, 212, 0.10)";
      ctx.shadowBlur = size * 0.006;
      ctx.stroke();
      ctx.restore();
    }
  }

  // Curseur carré
  if (shape === "square") {
    const h = r * 0.95;
    const p = phaseProgress;
    let dx = 0;
    let dy = 0;

    if (phase.name === "inhale") {
      dx = -h + p * 2 * h;
      dy = -h;
    } else if (phase.name === "hold1") {
      dx = h;
      dy = -h + p * 2 * h;
    } else if (phase.name === "exhale") {
      dx = h - p * 2 * h;
      dy = h;
    } else if (phase.name === "hold2") {
      dx = -h;
      dy = h - p * 2 * h;
    }

    ctx.save();
    ctx.beginPath();
    ctx.arc(cx + dx, cy + dy, size * 0.015, 0, Math.PI * 2);
    ctx.fillStyle = colors.main;
    ctx.fill();
    ctx.restore();
  }

  // Curseur triangle
  if (shape === "triangle") {
    const hw = r * 1.05;
    const hh = r * 0.88;
    const top = { x: cx, y: cy - hh };
    const br = { x: cx + hw, y: cy + hh };
    const bl = { x: cx - hw, y: cy + hh };

    let start;
    let end;

    if (phase.name === "inhale") {
      start = bl;
      end = top;
    } else if (phase.name === "hold") {
      start = top;
      end = br;
    } else {
      start = br;
      end = bl;
    }

    const p = phaseProgress;
    const tx = start.x + (end.x - start.x) * p;
    const ty = start.y + (end.y - start.y) * p;

    ctx.save();
    ctx.beginPath();
    ctx.arc(tx, ty, size * 0.015, 0, Math.PI * 2);
    ctx.fillStyle = colors.main;
    ctx.fill();
    ctx.restore();
  }

  // Label
  const isLight = isTransparentGif;
  drawShapeLabel(ctx, {
    shape,
    cx,
    cy,
    radius: r,
    label: phase.label,
  fillStyle:   "#FFFFFF",
  shadowColor: isLight ? "rgba(0,0,0,0)" : "rgba(0,0,0,0.55)",
  shadowBlur:  isLight ? 0 : size * 0.012,
  strokeStyle: isLight ? "rgba(54,73,91,0.95)" : null,
  strokeWidth: isLight ? Math.max(2, size * 0.004) : 0,
  });
}

// ─── Styles inline ───────────────────────────────────────────────────────────
const S = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(160deg, #0f172a 0%, #101827 100%)",
    color: "#f8fafc",
    padding: "32px 24px 64px",
    fontFamily: '"DM Sans", system-ui, sans-serif',
  },
  inner: { maxWidth: 960, margin: "0 auto" },
  h1: { fontSize: 26, fontWeight: 900, margin: "0 0 6px" },
  sub: {
    color: "rgba(255,255,255,0.52)",
    fontSize: 14,
    margin: "0 0 32px",
    lineHeight: 1.55,
  },
  cols: {
    display: "flex",
    gap: 44,
    flexWrap: "wrap",
    alignItems: "flex-start",
  },
  ctrl: { flex: "0 0 300px" },
  label: {
    display: "block",
    marginBottom: 8,
    fontWeight: 700,
    fontSize: 13,
    color: "rgba(255,255,255,0.78)",
  },
  select: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.1)",
    background: "rgba(255,255,255,0.06)",
    color: "#f8fafc",
    fontSize: 14,
  },
  row: { display: "flex", gap: 8, flexWrap: "wrap" },
  toggleBtn: (active) => ({
    flex: 1,
    minWidth: 0,
    padding: "10px 8px",
    borderRadius: 10,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 700,
    border: active ? "2px solid #76A5A7" : "2px solid rgba(255,255,255,0.08)",
    background: active ? "rgba(118,165,167,0.18)" : "rgba(255,255,255,0.04)",
    color: active ? "#f8fafc" : "rgba(255,255,255,0.65)",
  }),
  actionBtn: (accent) => ({
    width: "100%",
    padding: "13px",
    borderRadius: 10,
    border: "none",
    color: "#f8fafc",
    fontWeight: 800,
    fontSize: 14,
    cursor: "pointer",
    background: accent
      ? "linear-gradient(135deg, #61837A 0%, #76A5A7 100%)"
      : "rgba(118,165,167,0.18)",
  }),
  disabledBtn: {
    width: "100%",
    padding: "13px",
    borderRadius: 10,
    border: "none",
    color: "rgba(255,255,255,0.35)",
    fontWeight: 800,
    fontSize: 14,
    cursor: "not-allowed",
    background: "rgba(255,255,255,0.06)",
  },
  progressBar: {
    height: 4,
    background: "rgba(255,255,255,0.08)",
    borderRadius: 4,
    overflow: "hidden",
    marginTop: 10,
  },
  tip: {
    marginTop: 22,
    padding: 14,
    borderRadius: 12,
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.07)",
    fontSize: 12,
    lineHeight: 1.7,
    color: "rgba(255,255,255,0.55)",
  },
};

export default function BreathingExportPage() {
  const [selectedId, setSelectedId] = useState(1);
  const [cycles, setCycles] = useState(2);
  const [recording, setRecording] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewing, setPreviewing] = useState(false);
  const [exportMode, setExportMode] = useState("webm-green");

  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const intRef = useRef(null);

  const technique =
    TECHNIQUES.find((t) => t.id === selectedId) || TECHNIQUES[0];
  const totalDuration = technique.cycleDuration * cycles;

  const previewBackground = useMemo(
    () => MODE_INFO[exportMode]?.previewBg || "transparent",
    [exportMode]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    drawFrame(canvas.getContext("2d"), PREVIEW_SIZE, 0, technique, exportMode);
  }, [technique, exportMode]);

  useEffect(() => {
    return () => {
      cancelAnimationFrame(rafRef.current);
      clearInterval(intRef.current);
    };
  }, []);

  const stopPreview = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    setPreviewing(false);
  }, []);

  const startPreview = useCallback(() => {
    setPreviewing(true);
    const startMs = performance.now();

    const loop = (now) => {
      const t = (now - startMs) / 1000;
      const ctx = canvasRef.current?.getContext("2d");
      if (ctx) {
        drawFrame(ctx, PREVIEW_SIZE, t, technique, exportMode);
      }
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
  }, [technique, exportMode]);

const exportWebMGreen = useCallback(() => {
  const displayCanvas = canvasRef.current;
  if (!displayCanvas || recording) return;

  stopPreview();
  setRecording(true);
  setProgress(0);

  const recCanvas = document.createElement("canvas");
  recCanvas.width = WEBM_SIZE;
  recCanvas.height = WEBM_SIZE;
  const recCtx = recCanvas.getContext("2d", { alpha: false });

  // Canvas intermédiaire pour le compositing propre
  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = WEBM_SIZE;
  tempCanvas.height = WEBM_SIZE;
  const tempCtx = tempCanvas.getContext("2d", { alpha: true });

  const mimeTypes = [
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
  ];
  const mime =
    mimeTypes.find((m) => MediaRecorder.isTypeSupported(m)) || "video/webm";

  const stream = recCanvas.captureStream(WEBM_FPS);
  const recorder = new MediaRecorder(stream, {
    mimeType: mime,
    videoBitsPerSecond: 8_000_000,
  });

  const chunks = [];
  recorder.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data);
  };
  recorder.onstop = () => {
    const blob = new Blob(chunks, { type: mime.split(";")[0] });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `harmonauria-${technique.id}-${technique.name
      .replace(/[\s()]/g, "-")
      .toLowerCase()}-${cycles}c-canva-green.webm`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setRecording(false);
    setProgress(1);
  };

  recorder.start(100);

  let frame = 0;
  const totalFrames = Math.ceil(totalDuration * WEBM_FPS);
  const displayCtx = displayCanvas.getContext("2d");

  intRef.current = setInterval(() => {
    if (frame >= totalFrames) {
      clearInterval(intRef.current);
      recorder.stop();
      return;
    }

    const t = frame / WEBM_FPS;

    // ── PASSE 1 : dessin sur canvas transparent ──────────────────
    tempCtx.clearRect(0, 0, WEBM_SIZE, WEBM_SIZE);
    drawFrame(tempCtx, WEBM_SIZE, t, technique, "compose");

    // ── PASSE 2 : alpha-threshold → bords nets, zéro frange verte ─
    const imgData = tempCtx.getImageData(0, 0, WEBM_SIZE, WEBM_SIZE);
    const d = imgData.data;

    for (let i = 0; i < d.length; i += 4) {
      if (d[i + 3] < 200) {
        // Pixel semi-transparent → totalement transparent
        d[i] = d[i + 1] = d[i + 2] = d[i + 3] = 0;
      } else {
        // Pixel opaque → force l'alpha à 255
        d[i + 3] = 255;
      }
    }

    tempCtx.putImageData(imgData, 0, 0);

    // ── PASSE 3 : composite sur fond vert ────────────────────────
    recCtx.fillStyle = "#00FF00"; // vert pur pour chroma key
    recCtx.fillRect(0, 0, WEBM_SIZE, WEBM_SIZE);
    recCtx.drawImage(tempCanvas, 0, 0);

    // Mise à jour de l'aperçu (pas besoin de threshold pour l'affichage)
    drawFrame(displayCtx, PREVIEW_SIZE, t, technique, "webm-green");

    setProgress(frame / totalFrames);
    frame += 1;
  }, 1000 / WEBM_FPS);
}, [recording, technique, cycles, totalDuration, stopPreview]);

const exportWebMTransparent = useCallback(() => {
  const displayCanvas = canvasRef.current;
  if (!displayCanvas || recording) return;

  stopPreview();
  setRecording(true);
  setProgress(0);

  // Canvas alpha:true obligatoire pour VP9 alpha
  const recCanvas = document.createElement("canvas");
  recCanvas.width = WEBM_SIZE;
  recCanvas.height = WEBM_SIZE;
  const recCtx = recCanvas.getContext("2d", { alpha: true });

  // VP9 est le seul codec WebM qui supporte l'alpha
  const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
    ? "video/webm;codecs=vp9"
    : null;

  if (!mime) {
    alert("Votre navigateur ne supporte pas VP9 alpha. Utilisez Chrome.");
    setRecording(false);
    return;
  }

  const stream = recCanvas.captureStream(WEBM_FPS);
  const recorder = new MediaRecorder(stream, {
    mimeType: mime,
    videoBitsPerSecond: 8_000_000,
  });

  const chunks = [];
  recorder.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data);
  };
  recorder.onstop = () => {
    const blob = new Blob(chunks, { type: "video/webm" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `harmonauria-${technique.id}-${technique.name
      .replace(/[\s()]/g, "-")
      .toLowerCase()}-${cycles}c-transparent.webm`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setRecording(false);
    setProgress(1);
  };

  recorder.start(100);

  let frame = 0;
  const totalFrames = Math.ceil(totalDuration * WEBM_FPS);
  const displayCtx = displayCanvas.getContext("2d");
  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = WEBM_SIZE;
  tempCanvas.height = WEBM_SIZE;
  const tempCtx = tempCanvas.getContext("2d", { alpha: true });

  intRef.current = setInterval(() => {
    if (frame >= totalFrames) {
      clearInterval(intRef.current);
      recorder.stop();
      return;
    }

    const t = frame / WEBM_FPS;

    // Dessin sur fond transparent
    tempCtx.clearRect(0, 0, WEBM_SIZE, WEBM_SIZE);
    drawFrame(tempCtx, WEBM_SIZE, t, technique, "compose");

    // Alpha-threshold : bords nets, zéro semi-transparent
    const imgData = tempCtx.getImageData(0, 0, WEBM_SIZE, WEBM_SIZE);
    const d = imgData.data;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i + 3] < 200) {
        d[i] = d[i + 1] = d[i + 2] = d[i + 3] = 0;
      } else {
        d[i + 3] = 255;
      }
    }
    tempCtx.putImageData(imgData, 0, 0);

    // On copie sur le canvas enregistré (lui aussi alpha:true)
    recCtx.clearRect(0, 0, WEBM_SIZE, WEBM_SIZE);
    recCtx.drawImage(tempCanvas, 0, 0);

    // Aperçu
    drawFrame(displayCtx, PREVIEW_SIZE, t, technique, "gif-transparent");

    setProgress(frame / totalFrames);
    frame += 1;
  }, 1000 / WEBM_FPS);
}, [recording, technique, cycles, totalDuration, stopPreview]);

const exportTransparentGif = useCallback(async () => {
  if (recording) return;

  stopPreview();
  setRecording(true);
  setProgress(0);

  // ── Chargement dynamique de gif.js ──────────────────────────────
  if (!window.GIF) {
    await new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src =
        "https://cdn.jsdelivr.net/npm/gif.js@0.2.0/dist/gif.js";
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  // Chargement du worker comme blob (contourne les restrictions CORS)
  const workerBlob = await fetch(
    "https://cdn.jsdelivr.net/npm/gif.js@0.2.0/dist/gif.worker.js"
  ).then((r) => r.blob());
  const workerBlobUrl = URL.createObjectURL(workerBlob);

  // Magenta (#FF00FF) = couleur-clé pour la transparence GIF
  const MATTE_HEX = 0xff00ff;

  const gif = new window.GIF({
    workers: 2,
    quality: 5,
    width: GIF_SIZE,
    height: GIF_SIZE,
    workerScript: workerBlobUrl,
    transparent: MATTE_HEX,
    background: "#FF00FF",
  });

  const tempCanvas = document.createElement("canvas");
  tempCanvas.width = GIF_SIZE;
  tempCanvas.height = GIF_SIZE;
  const tempCtx = tempCanvas.getContext("2d", { alpha: true });
  const displayCtx = canvasRef.current?.getContext("2d");

  const totalFrames = Math.ceil(totalDuration * GIF_FPS);
  const GIF_TRANSPARENT_ALPHA_CUTOFF = 2;

  // ── Génération des frames ────────────────────────────────────────
  for (let frame = 0; frame < totalFrames; frame++) {
    const t = frame / GIF_FPS;

    // Dessin sur canvas transparent (contour uniquement)
    tempCtx.clearRect(0, 0, GIF_SIZE, GIF_SIZE);
    drawFrame(tempCtx, GIF_SIZE, t, technique, "gif-transparent");

    // Alpha-threshold : pixels semi-transparents → magenta (sera transparent dans le GIF)
    const imgData = tempCtx.getImageData(0, 0, GIF_SIZE, GIF_SIZE);
    const d = imgData.data;

    for (let i = 0; i < d.length; i += 4) {
      if (d[i + 3] <= GIF_TRANSPARENT_ALPHA_CUTOFF) {
        d[i] = 255;
        d[i + 1] = 0;
        d[i + 2] = 255;
        d[i + 3] = 255;
      } else {
        d[i + 3] = 255;
      }
    }

    tempCtx.putImageData(imgData, 0, 0);

    gif.addFrame(tempCanvas, {
      delay: Math.round(1000 / GIF_FPS),
      copy: true,     // copie le canvas au moment de l'ajout
      dispose: 2,     // restore to background (matte) entre chaque frame
    });

    // Aperçu pendant la génération
    if (displayCtx) {
      drawFrame(displayCtx, PREVIEW_SIZE, t, technique, "gif-transparent");
    }

    setProgress((frame / totalFrames) * 0.85);

    // Cède la main au navigateur toutes les 10 frames
    if (frame % 10 === 0) {
      await new Promise((r) => setTimeout(r, 0));
    }
  }

  setProgress(0.9);

  // ── Encodage GIF ─────────────────────────────────────────────────
  gif.on("progress", (p) => setProgress(0.9 + p * 0.1));

  gif.on("finished", (blob) => {
    URL.revokeObjectURL(workerBlobUrl);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `harmonauria-${technique.id}-${technique.name
      .replace(/[\s()]/g, "-")
      .toLowerCase()}-${cycles}c-outline-transparent.gif`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setRecording(false);
    setProgress(1);
  });

  gif.on("error", (err) => {
    console.error("Erreur export GIF :", err);
    URL.revokeObjectURL(workerBlobUrl);
    setRecording(false);
  });

  gif.render();
}, [recording, technique, cycles, totalDuration, stopPreview]);

const handleExport = useCallback(() => {
  if (recording) return;
  if (exportMode === "gif-transparent")  { exportTransparentGif();    return; }
  if (exportMode === "webm-transparent") { exportWebMTransparent();   return; }
  exportWebMGreen();
}, [recording, exportMode, exportTransparentGif, exportWebMTransparent, exportWebMGreen]);

  return (
    <div style={S.page}>
      <div style={S.inner}>
        <h1 style={S.h1}>Export Animation Respiration</h1>
        <p style={S.sub}>
          Deux sorties séparées : un <strong style={{ color: "rgba(255,255,255,0.78)" }}>GIF contour transparent</strong>
          {" "}avec intérieur réellement vide, et un{" "}
          <strong style={{ color: "rgba(255,255,255,0.78)" }}>WebM fond vert Canva</strong>
          {" "}pour l’effaceur d’arrière-plan.
        </p>

        <div style={S.cols}>
          <div style={S.ctrl}>
            <label style={S.label}>Technique</label>
            <select
              value={selectedId}
              onChange={(e) => {
                stopPreview();
                setSelectedId(Number(e.target.value));
              }}
              style={{ ...S.select, marginBottom: 24 }}
            >
              {TECHNIQUES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>

            <label style={S.label}>Mode d’export</label>
            <div style={{ ...S.row, marginBottom: 24 }}>
{[
  { value: "gif-transparent",  label: "GIF transparent" },
  { value: "webm-transparent", label: "WebM transparent" },
  { value: "webm-green",       label: "WebM fond vert" },
].map((opt) => (
  <button
    key={opt.value}
    onClick={() => { stopPreview(); setExportMode(opt.value); }}
    style={S.toggleBtn(exportMode === opt.value)}
  >
    {opt.label}
  </button>
))}
            </div>

            <label style={S.label}>
              Cycles{" "}
              <span
                style={{
                  fontWeight: 500,
                  color: "rgba(255,255,255,0.35)",
                }}
              >
                ({cycles} × {technique.cycleDuration}s = {totalDuration}s)
              </span>
            </label>

            <input
              type="range"
              min={1}
              max={6}
              step={1}
              value={cycles}
              onChange={(e) => setCycles(Number(e.target.value))}
              style={{ width: "100%", marginBottom: 4 }}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: 11,
                color: "rgba(255,255,255,0.3)",
                marginBottom: 28,
              }}
            >
              <span>1</span>
              <span>6</span>
            </div>

            <button
              onClick={previewing ? stopPreview : startPreview}
              disabled={recording}
              style={{ ...S.actionBtn(false), marginBottom: 10 }}
            >
              {previewing ? "⏹ Arrêter l’aperçu" : "▶ Aperçu"}
            </button>

            {recording ? (
              <div style={S.disabledBtn}>
                ⏳ Export en cours… {Math.round(progress * 100)}%
              </div>
            ) : (
              <button onClick={handleExport} style={S.actionBtn(true)}>
                ⬇ Exporter{" "}
                {exportMode === "gif-transparent"
                  ? "GIF transparent"
                  : "WebM fond vert"}{" "}
                — {totalDuration}s
              </button>
            )}

            {recording && (
              <div style={S.progressBar}>
                <div
                  style={{
                    height: "100%",
                    width: `${progress * 100}%`,
                    background: "#76A5A7",
                    transition: "width 0.1s linear",
                  }}
                />
              </div>
            )}

            <div style={S.tip}>
              <p
                style={{
                  margin: "0 0 6px",
                  fontWeight: 700,
                  color: "rgba(255,255,255,0.7)",
                }}
              >
                💡 Conseils
              </p>

              <p style={{ margin: "0 0 6px" }}>
                • <strong style={{ color: "rgba(255,255,255,0.72)" }}>GIF transparent</strong> :
                traits propres, intérieur vide, sans glow. C’est la meilleure version GIF possible ici.
              </p>

<p style={{ margin: "0 0 6px" }}>
  • <strong style={{ color: "rgba(255,255,255,0.72)" }}>WebM transparent</strong> :
  vidéo VP9 avec alpha natif. Importe dans Canva → se place sur la timeline → synchronisation parfaite. Chrome uniquement.
</p>

<p style={{ margin: "0 0 6px" }}>
  • <strong style={{ color: "rgba(255,255,255,0.72)" }}>WebM fond vert</strong> :
  fallback si le transparent ne marche pas dans Canva. Applique l'effaceur d'arrière-plan.
</p>

              <p style={{ margin: "0 0 6px" }}>
                • Le GIF peut rester limité dans Canva à cause du format lui-même.
              </p>

              <p style={{ margin: 0 }}>
                • L’export prend exactement {totalDuration}s de contenu.
              </p>
            </div>
          </div>

          <div style={{ flex: 1, minWidth: 240 }}>
            <div
              style={{
                position: "relative",
                display: "inline-block",
                width: "100%",
                maxWidth: 420,
              }}
            >
              <canvas
                ref={canvasRef}
                width={PREVIEW_SIZE}
                height={PREVIEW_SIZE}
                style={{
                  width: "100%",
                  height: "auto",
                  display: "block",
                  borderRadius: 20,
                  border: "1px solid rgba(255,255,255,0.08)",
                  background:
                    previewBackground === "transparent"
                      ? "repeating-conic-gradient(#1e293b 0% 25%, #0f172a 0% 50%) 0 0 / 20px 20px"
                      : "transparent",
                }}
              />

              {!previewing && !recording && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "rgba(0,0,0,0.32)",
                    borderRadius: 20,
                    backdropFilter: "blur(2px)",
                  }}
                >
                  <button
                    onClick={startPreview}
                    style={{
                      padding: "14px 30px",
                      borderRadius: 12,
                      border: "none",
                      background: "rgba(255,255,255,0.12)",
                      color: "#f8fafc",
                      fontWeight: 800,
                      fontSize: 16,
                      cursor: "pointer",
                      backdropFilter: "blur(8px)",
                    }}
                  >
                    ▶ Aperçu
                  </button>
                </div>
              )}
            </div>

            <p
              style={{
                marginTop: 10,
                fontSize: 12,
                color: "rgba(255,255,255,0.28)",
                textAlign: "center",
              }}
            >
              {exportMode === "gif-transparent"
                ? `${GIF_SIZE} × ${GIF_SIZE}px · GIF transparent contour`
                : `${WEBM_SIZE} × ${WEBM_SIZE}px · WebM fond vert Canva`}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}